/**
 * Mock API service for tracking viewed indicator state
 * Uses sessionStorage for persistence, structured for easy swap to real API
 */

/**
 * Custom event name for when an indicator is marked as viewed
 */
export const INDICATOR_VIEWED_EVENT = 'indicator-viewed';

/**
 * Dispatch a custom event when an indicator is viewed
 */
function dispatchIndicatorViewedEvent(jotdocId: string, indicatorId: string): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(INDICATOR_VIEWED_EVENT, {
        detail: { jotdocId, indicatorId },
      }),
    );
  }
}

/**
 * Get all viewed indicator IDs for a jotdoc
 * @param jotdocId The jotdoc ID
 * @returns Promise resolving to a Set of viewed indicator IDs
 */
export async function getViewedIndicators(jotdocId: string): Promise<Set<string>> {
  // TODO: Replace with real API call when backend is ready
  // Example: const response = await api.get(`/jotdocs/${jotdocId}/viewed-indicators`);
  // return new Set(response.data.viewedIndicatorIds);

  if (typeof window === 'undefined') {
    return new Set();
  }

  try {
    const key = `jotdoc-viewed-indicators-${jotdocId}`;
    const stored = sessionStorage.getItem(key);
    if (stored) {
      const viewedIds = JSON.parse(stored) as string[];
      return new Set(viewedIds);
    }
  } catch (error) {
    console.error('Error reading viewed indicators from sessionStorage:', error);
  }

  return new Set();
}

/**
 * Mark an indicator as viewed
 * @param jotdocId The jotdoc ID
 * @param indicatorId The indicator ID to mark as viewed
 */
export async function markIndicatorViewed(
  jotdocId: string,
  indicatorId: string,
): Promise<void> {
  // TODO: Replace with real API call when backend is ready
  // Example: await api.post(`/jotdocs/${jotdocId}/viewed-indicators`, { indicatorId });

  if (typeof window === 'undefined') {
    return;
  }

  try {
    const key = `jotdoc-viewed-indicators-${jotdocId}`;
    const viewedSet = await getViewedIndicators(jotdocId);
    viewedSet.add(indicatorId);
    const viewedArray = Array.from(viewedSet);
    sessionStorage.setItem(key, JSON.stringify(viewedArray));
    dispatchIndicatorViewedEvent(jotdocId, indicatorId);
  } catch (error) {
    console.error('Error saving viewed indicator to sessionStorage:', error);
  }
}

/**
 * Mark multiple indicators as viewed in a single operation
 * @param jotdocId The jotdoc ID
 * @param indicatorIds Array of indicator IDs to mark as viewed
 */
export async function markIndicatorsViewed(
  jotdocId: string,
  indicatorIds: string[],
): Promise<void> {
  // TODO: Replace with real API call when backend is ready
  // Example: await api.post(`/jotdocs/${jotdocId}/viewed-indicators/batch`, { indicatorIds });

  if (typeof window === 'undefined') {
    return;
  }

  try {
    const key = `jotdoc-viewed-indicators-${jotdocId}`;
    const viewedSet = await getViewedIndicators(jotdocId);
    indicatorIds.forEach(id => viewedSet.add(id));
    const viewedArray = Array.from(viewedSet);
    sessionStorage.setItem(key, JSON.stringify(viewedArray));
  } catch (error) {
    console.error('Error saving viewed indicators to sessionStorage:', error);
  }
}

